import { useState } from 'react'
import type { FormEvent } from 'react'

import { cn } from '@/lib/utils'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { Button } from '@/shared/ui/Button'
import { controlVariants } from '@/shared/ui/Form/control-styles'

import { useScheduleCheckIn } from '../hooks/useScheduleCheckIn'

import type { Session } from '../model/types'

/** Âncora de ensino e de prova do campo da palavra — design.md §6.3. */
export const SCHEDULE_KEYWORD_CHECK_IN_ANCHOR = 'programacao.check-in-palavra'

const FIELD_ID = 'schedule-check-in-keyword'
const ERROR_ID = `${FIELD_ID}-error`

type ScheduleKeywordCheckInControlsProps = {
  session: Session
  onFinished?: () => void
}

/**
 * Check-in da sessão por palavra digitada.
 *
 * O palestrante fala a palavra no palco e o participante digita — não há
 * câmera, e isso é a entrega, não uma omissão: a operação testou QR em palestra
 * nos eventos reais e ele não funciona (código projetado longe, luz ruim,
 * permissão de câmera no meio da fala). O QR de parceiro e de missão segue como
 * está; só o da programação saiu.
 *
 * Rótulo, campo e botão ficam **empilhados**, como em
 * `MissionQrCodeEvidenceControls`: a diretriz L2 do design system só admite um
 * elemento fixo ao lado de uma linha com texto, e a 375px campo mais botão não
 * cabem na mesma linha. O raio vem de `--control-r-lg`, o token do próprio
 * tamanho do controle — L1, raio é proporção da caixa, não constante.
 */
export const ScheduleKeywordCheckInControls = ({
  session,
  onFinished
}: ScheduleKeywordCheckInControlsProps) => {
  const { t } = useTranslation()
  const [answer, setAnswer] = useState('')
  const { status, errorMessage, submit } = useScheduleCheckIn({ onFinished })

  const isSubmitting = status === 'loading'
  const hasError = Boolean(errorMessage)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void submit(session, answer)
  }

  return (
    <form className="flex flex-col gap-2" onSubmit={handleSubmit} noValidate>
      <label htmlFor={FIELD_ID} className="text-base text-gray-700">
        {t('schedule.checkIn.label')}
      </label>

      <input
        id={FIELD_ID}
        data-testid={SCHEDULE_KEYWORD_CHECK_IN_ANCHOR}
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="send"
        placeholder={t('schedule.checkIn.placeholder')}
        className={cn(controlVariants({ size: 'lg' }))}
        value={answer}
        onChange={event => setAnswer(event.target.value)}
        disabled={isSubmitting}
        aria-invalid={hasError}
        aria-describedby={hasError ? ERROR_ID : undefined}
      />

      {hasError && (
        <p
          id={ERROR_ID}
          role="alert"
          className="text-sm font-medium text-danger-text"
        >
          {errorMessage}
        </p>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        showTrailingIcon={false}
        className="w-full justify-center"
        disabled={isSubmitting || answer.trim().length === 0}
      >
        {isSubmitting
          ? t('schedule.checkIn.submitting')
          : t('schedule.checkIn.submit')}
      </Button>
    </form>
  )
}
